import { useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { Loader2, Plus, X, Edit2, Car, Bike, Wallet, Building2, Trash2 } from "lucide-react";

export default function Designations() {
  const [items, setItems] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showDeptNew, setShowDeptNew] = useState(false);

  const load = async () => {
    setLoading(true);
    const [d, dp] = await Promise.all([
      api.get("/designations"),
      api.get("/departments"),
    ]);
    setItems(d.data); setDepartments(dp.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const removeDept = async (id) => {
    if (!window.confirm("Delete this department?")) return;
    try { await api.delete(`/departments/${id}`); load(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  return (
    <div className="space-y-6" data-testid="designations-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="kpi-label" style={{ color: "var(--color-secondary)" }}>Organisation Config</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Departments & Designations</h1>
          <p className="text-stone-600 mt-1 text-sm">Departments group employees · Designations carry TA/DA entitlements.</p>
        </div>
      </header>

      {/* DEPARTMENTS */}
      <div className="card-flat" data-testid="departments-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4" style={{ color: "var(--color-primary)" }} />
            <div className="kpi-label">Departments · {departments.length}</div>
          </div>
          <button onClick={() => setShowDeptNew(true)} data-testid="add-department-button"
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md text-white text-xs font-semibold" style={{ background: "var(--color-primary)" }}>
            <Plus className="w-3.5 h-3.5" /> Add Department
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {departments.length === 0 && <div className="text-sm text-stone-500">No departments yet.</div>}
          {departments.map((d) => (
            <div key={d.id} data-testid={`dept-${d.id}`}
                 className="inline-flex items-center gap-2 pl-3 pr-1 py-1.5 rounded-full text-sm border border-stone-200 bg-white">
              <span className="font-medium">{d.name}</span>
              <span className="text-xs text-stone-500 font-mono">{d.code}</span>
              <button onClick={() => removeDept(d.id)} className="p-1 rounded-full hover:bg-red-50 text-red-600"
                      data-testid={`del-dept-${d.id}`}>
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* DESIGNATIONS */}
      <div className="flex items-center justify-between">
        <div>
          <div className="kpi-label" style={{ color: "var(--color-secondary)" }}>Compensation Rules</div>
          <h2 className="font-heading text-xl font-bold tracking-tight mt-1">Designation Entitlements</h2>
          <p className="text-stone-600 mt-1 text-xs">TA/DA caps & vehicle entitlement per designation — used during claim entry.</p>
        </div>
        <button onClick={() => setShowNew(true)} data-testid="add-designation-button"
                className="flex items-center gap-2 px-4 py-2 rounded-md text-white font-medium" style={{ background: "var(--color-primary)" }}>
          <Plus className="w-4 h-4" /> Add Designation
        </button>
      </div>

      {loading ? <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="designations-grid">
          {items.map((d) => (
            <div key={d.id} className="card-flat" data-testid={`des-${d.name.replace(/\s/g, "-")}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-heading text-lg font-bold">{d.name}</div>
                  <div className="text-xs tracking-wider text-stone-500 mt-0.5">LEVEL L{d.level}</div>
                </div>
                <button onClick={() => setEdit(d)} className="p-1.5 rounded hover:bg-stone-100 text-stone-500"
                        data-testid={`edit-${d.name.replace(/\s/g, "-")}`}>
                  <Edit2 className="w-4 h-4" />
                </button>
              </div>
              <div className="mt-4 space-y-2 text-sm">
                <Row icon={d.vehicle_type === "Car" ? Car : d.vehicle_type === "Bike" ? Bike : Wallet}
                     label="Vehicle" v={d.vehicle_type} />
                <Row label="Per KM" v={`₹${d.per_km_rate}`} />
                <Row label="Daily DA" v={formatINR(d.daily_da)} />
                <Row label="Hotel cap/day" v={formatINR(d.hotel_cap_per_day)} />
                <Row label="Food cap/day" v={formatINR(d.food_cap_per_day)} />
              </div>
            </div>
          ))}
        </div>
      )}

      {(edit || showNew) && (
        <Modal initial={edit} onClose={() => { setEdit(null); setShowNew(false); }}
               onSaved={() => { setEdit(null); setShowNew(false); load(); }} />
      )}
      {showDeptNew && (
        <DeptModal onClose={() => setShowDeptNew(false)}
                   onSaved={() => { setShowDeptNew(false); load(); }} />
      )}
    </div>
  );
}

function DeptModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", code: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await api.post("/departments", form); onSaved(); }
    catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-stone-900/60 flex items-center justify-center p-4" data-testid="dept-modal">
      <div className="bg-white rounded-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-xl font-bold">Add Department</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <In label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
          <In label="Code (optional)" value={form.code} onChange={(v) => setForm({ ...form, code: v })} placeholder="auto-generated if empty" />
          {err && <div className="text-sm px-3 py-2 rounded-md" style={{ background: "#FADED7", color: "#9A3412" }}>{err}</div>}
          <button type="submit" disabled={busy} data-testid="save-department-button"
                  className="w-full py-2 rounded-md text-white font-semibold" style={{ background: "var(--color-primary)" }}>
            {busy ? "Saving…" : "Save"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Row({ label, v, icon: Icon }) {
  return (
    <div className="flex items-center justify-between py-1 border-b border-stone-100 last:border-0">
      <div className="flex items-center gap-2 text-stone-600">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        <span className="text-xs uppercase tracking-wider">{label}</span>
      </div>
      <div className="font-medium text-sm">{v}</div>
    </div>
  );
}

function Modal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || {
    name: "", level: 1, vehicle_type: "None",
    per_km_rate: 0, daily_da: 0, hotel_cap_per_day: 0, food_cap_per_day: 0,
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    const body = {
      name: form.name, level: Number(form.level), vehicle_type: form.vehicle_type,
      per_km_rate: Number(form.per_km_rate), daily_da: Number(form.daily_da),
      hotel_cap_per_day: Number(form.hotel_cap_per_day), food_cap_per_day: Number(form.food_cap_per_day),
    };
    if (initial) await api.put(`/designations/${initial.id}`, body);
    else await api.post("/designations", body);
    setBusy(false); onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/50 flex items-center justify-center p-4" data-testid="designation-modal">
      <div className="bg-white rounded-xl w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-xl font-bold">{initial ? "Edit" : "New"} Designation</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          <In label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <In label="Level (1-5)" type="number" value={form.level} onChange={(v) => setForm({ ...form, level: v })} />
          <Sel label="Vehicle Type" value={form.vehicle_type} onChange={(v) => setForm({ ...form, vehicle_type: v })}>
            <option>None</option><option>Bike</option><option>Car</option>
          </Sel>
          <In label="Per KM Rate ₹" type="number" value={form.per_km_rate} onChange={(v) => setForm({ ...form, per_km_rate: v })} />
          <In label="Daily DA ₹" type="number" value={form.daily_da} onChange={(v) => setForm({ ...form, daily_da: v })} />
          <In label="Hotel cap/day ₹" type="number" value={form.hotel_cap_per_day} onChange={(v) => setForm({ ...form, hotel_cap_per_day: v })} />
          <In label="Food cap/day ₹" type="number" value={form.food_cap_per_day} onChange={(v) => setForm({ ...form, food_cap_per_day: v })} />
          <button type="submit" disabled={busy} data-testid="save-designation-button"
                  className="col-span-2 py-2 mt-2 rounded-md text-white font-medium" style={{ background: "var(--color-primary)" }}>
            {busy ? "Saving…" : "Save"}
          </button>
        </form>
      </div>
    </div>
  );
}

function In({ label, type = "text", value, onChange }) {
  return <label><div className="kpi-label mb-1.5">{label}</div>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={label === "Name"}
           className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm focus:outline-none focus:border-primary" />
  </label>;
}
function Sel({ label, value, onChange, children }) {
  return <label><div className="kpi-label mb-1.5">{label}</div>
    <select value={value} onChange={(e) => onChange(e.target.value)}
            className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm bg-white focus:outline-none focus:border-primary">{children}</select>
  </label>;
}
